import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  FileContext,
  HandoverResultDto,
  HandoverViewDto,
  PickPlanDto,
  RequisitionDetailDto,
  RequisitionDto,
  StoredFileDto,
} from '@eombor/contracts';
import { accountingApi } from '../../accounting/api/accounting.api.js';
import { notificationKeys } from '../../notification/model/notification.queries.js';
import { requisitionApi } from '../../requisition/api/requisition.api.js';
import {
  newIdempotencyKey,
  requisitionKeys,
} from '../../requisition/model/requisition.queries.js';
import {
  handoverApi,
  type ConfirmReceiptBody,
  type CreateHandoverBody,
  type PositionDto,
} from '../api/handover.api.js';

/**
 * Berish oqimining so'rovlari.
 *
 * Kalitlar ARIZA kalitlari bilan bir oilada emas, lekin har berishdan keyin
 * talabnoma kalitlari BEKOR QILINADI: dalolatnoma yozilishi bilan talabnomaning
 * holati, satrlardagi berilgan miqdor va navbat ham o'zgaradi.
 */
export const handoverKeys = {
  all: ['handover'] as const,
  ofRequisition: (id: string) => ['handover', 'list', id] as const,
  positions: ['handover', 'positions'] as const,
  /** Berishga tayyor talabnomalar — skanerlangan kod shular ichidan qidiriladi. */
  ready: ['handover', 'ready'] as const,
  /** Yig'ib olish rejasi — berish rejimining yagona so'rovi. */
  pick: (id: string) => ['handover', 'pick', id] as const,
};

/**
 * YIG'IB OLISH REJASI.
 *
 * Kesh YO'Q (`staleTime: 0`): reja manzil va miqdorni aytadi, ular esa
 * boshqa skladchining ishidan o'zgarib turadi. Eskirgan reja skladchini
 * bo'sh javonga yuborardi — bu ekranning eng yomon xatosi.
 */
export const usePickPlan = (requisitionId: string | null): UseQueryResult<PickPlanDto> =>
  useQuery({
    queryKey: handoverKeys.pick(requisitionId ?? ''),
    queryFn: () => handoverApi.pickPlan(requisitionId ?? ''),
    enabled: requisitionId !== null,
    staleTime: 0,
  });

export const useHandovers = (
  requisitionId: string | null,
): UseQueryResult<HandoverViewDto[]> =>
  useQuery({
    queryKey: handoverKeys.ofRequisition(requisitionId ?? ''),
    queryFn: () => handoverApi.list(requisitionId ?? ''),
    enabled: requisitionId !== null,
    /*
     * Dalolatnoma O'ZGARMAS hujjat: yozilgandan keyin unga na rasm
     * qo'shiladi, na izoh o'zgaradi. Rasm yo'llari ham endi muddatli
     * emas (`files/<uuid>`), shuning uchun kesh qisqa bo'lishining sababi
     * qolmadi — ilgari u imzolangan havola 5 daqiqada o'lgani uchun
     * qisqartirilgan edi.
     */
    staleTime: 30 * 60_000,
  });

export const usePositions = (): UseQueryResult<PositionDto[]> =>
  useQuery({
    queryKey: handoverKeys.positions,
    queryFn: () => handoverApi.positions(),
    // Ma'lumotnoma kunlab o'zgarmaydi — har ekran ochilganda so'ramaymiz.
    staleTime: 30 * 60_000,
  });

/**
 * Berishga TAYYOR talabnomalar.
 *
 * Skanerlangan kod shu ro'yxatdan qidiriladi va bu ataylab: hujjat raqami
 * bo'yicha alohida endpoint yo'q, bo'lganda ham skladchi baribir faqat
 * `READY_FOR_PICKUP` talabnomani bera oladi. Ro'yxat qisqa (bir omborda bir
 * vaqtda o'nlab talabnoma), shuning uchun qidiruv mijozda ham arzon.
 */
export const useReadyRequisitions = (): UseQueryResult<RequisitionDto[]> =>
  useQuery({
    queryKey: handoverKeys.ready,
    queryFn: async () =>
      (await requisitionApi.list({ status: 'READY_FOR_PICKUP', limit: 200 })).items,
  });

/**
 * Berishga tayyor talabnoma — TREBOVANIYA raqami va SATRLARI bilan.
 *
 * Ro'yxatning o'zi bularni bermaydi: talabnomalar javobida satrlar yo'q,
 * trebovaniya esa umuman boshqa kontekstning hujjati. Lekin skladchi
 * uchun aynan shu ikkisi kerak — u qo'lidagi qog'ozdagi raqamni topadi va
 * nima berishini oldindan ko'radi. Raqamsiz ro'yxat unga "shu talabnoma
 * o'shanikimi?" degan savolni har safar berardi.
 */
export interface ReadyHandoverRow {
  requisition: RequisitionDto;
  /** Qog'ozdagi «ТРЕБОВАНИЕ №» — hali qo'yilmagan bo'lsa `null`. */
  formNo: string | null;
  /** Beriladigan satrlar — miqdor `effectiveQtyOf` qoidasi bo'yicha. */
  lines: { id: string; nomenclatureId: string; qty: number }[];
}

export const useReadyHandovers = (): {
  rows: ReadyHandoverRow[];
  isPending: boolean;
} => {
  const ready = useReadyRequisitions();
  const items = ready.data ?? [];

  /*
   * Har talabnoma uchun ALOHIDA ikki so'rov. Navbat qisqa (bir omborda bir
   * vaqtda o'nlab talabnoma) va so'rovlar keshlanadi, shuning uchun bu
   * arzon. Yig'ma endpoint yozish esa `handover` uchun ikkita kontekstni
   * bir-biriga bog'lab qo'yardi.
   */
  const details = useQueries({
    queries: items.map((row) => ({
      queryKey: requisitionKeys.byId(row.id),
      queryFn: () => requisitionApi.byId(row.id),
    })),
  });

  const surveys = useQueries({
    queries: items.map((row) => ({
      queryKey: ['accounting', 'surveys', { requisitionId: row.id, limit: 1 }] as const,
      queryFn: () => accountingApi.listSurveys({ requisitionId: row.id, limit: 1 }),
    })),
  });

  const rows = items.map((requisition, index) => ({
    requisition,
    formNo: surveys[index]?.data?.items[0]?.formNo ?? null,
    lines: (details[index]?.data?.lines ?? []).map((line) => ({
      id: line.id,
      nomenclatureId: line.nomenclatureId,
      /* Tasdiqlangan miqdor `null` bo'lsa so'ralgani olinadi — backenddagi
         `effectiveQtyOf` bilan bir xil qoida. */
      qty: line.approvedQty ?? line.orgApprovedQty ?? line.requestedQty,
    })),
  }));

  return { rows, isPending: ready.isPending };
};

/**
 * Skanerlangan kodga mos talabnoma.
 *
 * Kod hujjat raqami (`A-2026-000007`) ham, identifikator ham bo'lishi mumkin:
 * etiketkada qaysi biri bo'lishi omborga qarab farq qiladi. Katta-kichik harf
 * va chetdagi bo'shliqlar e'tiborga olinmaydi — skaner ba'zan bo'sh joy
 * qo'shib yuboradi.
 */
export const matchRequisition = (
  rows: readonly RequisitionDto[],
  code: string,
): RequisitionDto | null => {
  const needle = code.trim().toLowerCase();
  if (needle === '') return null;
  return (
    rows.find((row) => row.docNo.toLowerCase() === needle || row.id === needle) ??
    // Etiketkada raqam prefiksi bilan chop etilgan bo'lishi mumkin.
    rows.find((row) => row.docNo.toLowerCase().endsWith(needle)) ??
    null
  );
};

/**
 * Kodni TREBOVANIYA raqami bo'yicha ham qidiradi.
 *
 * Skladchining qo'lidagi qog'ozda talabnoma raqami emas, buxgalter qo'ygan
 * «ТРЕБОВАНИЕ №» yozilgan — va u odam birinchi navbatda aynan shuni yozadi.
 * Talabnoma raqami bo'yicha qidiruv ham qoladi: etiketkada u chop etiladi.
 */
export const matchReady = (
  rows: readonly ReadyHandoverRow[],
  code: string,
): RequisitionDto | null => {
  const needle = code.trim().toLowerCase();
  if (needle === '') return null;

  const byForm = rows.find((row) => row.formNo?.toLowerCase() === needle);
  if (byForm !== undefined) return byForm.requisition;

  return matchRequisition(
    rows.map((row) => row.requisition),
    code,
  );
};

export const useUploadPhoto = (): UseMutationResult<
  StoredFileDto,
  unknown,
  { file: File; context: FileContext }
> =>
  useMutation({
    mutationFn: ({ file, context }) => handoverApi.uploadPhoto(file, context),
  });

/** Berishdan keyin talabnoma ham, navbat ham, dalolatnomalar ham yangilanadi. */
const useHandoverSuccess = (): ((requisitionId: string, result: HandoverResultDto) => void) => {
  const client = useQueryClient();
  return (requisitionId, result) => {
    client.setQueryData<RequisitionDetailDto>(requisitionKeys.byId(requisitionId), {
      ...result.requisition,
      availableTransitions: result.availableTransitions,
    });
    void client.invalidateQueries({ queryKey: handoverKeys.all });
    void client.invalidateQueries({ queryKey: requisitionKeys.list({}), exact: false });
    void client.invalidateQueries({ queryKey: requisitionKeys.inbox });
    void client.invalidateQueries({ queryKey: notificationKeys.all });
  };
};

/**
 * BERISH.
 *
 * `Idempotency-Key` HAR URINISH uchun emas, har MUTATSIYA uchun bir marta
 * yasaladi: tarmoq uzilib qayta yuborilganda server takroriy so'rovni tanib
 * olishi va ikkinchi dalolatnoma yozmasligi kerak.
 */
export const useIssueHandover = (
  requisitionId: string,
): UseMutationResult<HandoverResultDto, unknown, CreateHandoverBody> => {
  const onDone = useHandoverSuccess();
  return useMutation({
    mutationFn: (body: CreateHandoverBody) =>
      handoverApi.issue(requisitionId, body, newIdempotencyKey()),
    onSuccess: (result) => onDone(requisitionId, result),
  });
};

export const useConfirmReceipt = (
  requisitionId: string,
): UseMutationResult<HandoverResultDto, unknown, ConfirmReceiptBody> => {
  const onDone = useHandoverSuccess();
  return useMutation({
    mutationFn: (body: ConfirmReceiptBody) =>
      handoverApi.confirmReceipt(requisitionId, body, newIdempotencyKey()),
    onSuccess: (result) => onDone(requisitionId, result),
  });
};
